import { array, bool, InferType, number, object, string } from "yup";
import { ExternalPropertySchema, FeedConnectionSchema } from "../../../types";
import { UserFeedDisabledCode } from "./UserFeedDisabledCode";
import { UserFeedHealthStatus } from "./UserFeedHealthStatus";
import { UserFeedManagerInviteType, UserFeedManagerStatus } from "../../../constants";

export const UserFeedSchema = object({
  id: string().required(),
  title: string().required(),
  url: string().required(),
  inputUrl: string(),
  /**
   * Optional workspace ownership. Null/undefined = personal feed; set = the workspace
   * that owns the feed.
   */
  workspaceId: string().nullable().optional(),
  /**
   * Authoritative flag for workspace-owned feeds. Per-user feed management invites
   * are disabled for these; access is managed through workspace membership instead.
   */
  isWorkspaceFeed: bool(),
  sharedAccessDetails: object({
    inviteId: string().required(),
  }).optional(),
  passingComparisons: array(string().required()).optional().default(undefined),
  blockingComparisons: array(string().required()).optional().default(undefined),
  externalProperties: array(ExternalPropertySchema.required()).nullable(),
  createdAt: string()
    .transform((value) => (value ? new Date(value).toISOString() : value))
    .required(),
  updatedAt: string().transform((value) => (value ? new Date(value).toISOString() : value)),
  disabledCode: string().oneOf(Object.values(UserFeedDisabledCode)).optional(),
  refreshRateOptions: array(
    object({
      rateSeconds: number().required(),
      disabledCode: string().default(undefined),
    }).required(),
  ).required(),
  healthStatus: string().oneOf(Object.values(UserFeedHealthStatus)).required(),
  connections: array(FeedConnectionSchema).required(),
  refreshRateSeconds: number().required(),
  userRefreshRateSeconds: number(),
  scheduleMode: string().oneOf(["interval", "scheduled"]).optional(),
  schedule: object({
    times: array(string().required()).required(),
    timezone: string().required(),
    days: array(number().required()).optional(),
  })
    .nullable()
    .optional(),
  formatOptions: object({
    dateFormat: string().optional().default(undefined),
    dateTimezone: string().optional().default(undefined),
    dateLocale: string().optional().default(undefined),
  })
    .optional()
    .notRequired()
    .default(null),
  dateCheckOptions: object({
    oldArticleDateDiffMsThreshold: number().optional().default(undefined),
  }).optional(),
  isLegacyFeed: bool(),
  shareManageOptions: object({
    invites: array(
      object({
        id: string().required(),
        discordUserId: string().required(),
        type: string().oneOf(Object.values(UserFeedManagerInviteType)).optional(),
        status: string().oneOf(Object.values(UserFeedManagerStatus)).optional().nullable(),
        createdAt: string()
          .transform((value) => (value ? new Date(value).toISOString() : value))
          .required(),
        connections: array(
          object({
            connectionId: string().required(),
          }).required(),
        ).nullable(),
      }).required(),
    ).required(),
  })
    .optional()
    .nullable()
    .default(undefined),
});

export type UserFeed = InferType<typeof UserFeedSchema>;
